import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import './GuardianPayment.css';

const GuardianPayment = () => {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const { token } = useParams();

  useEffect(() => {
    if (token) {
      loadPaymentData();
    }
  }, [token]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);

      // Make direct fetch call without authentication (public route)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-pay/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setPaymentData(data.data);
      } else {
        toast.error(data.message || 'Failed to load payment information');
      }
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Failed to load payment information. The payment link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);

      // Make direct fetch call without authentication (public route)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-pay/${token}/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data.redirectUrl) {
        // Redirect to Nuapay payment page
        window.location.href = data.data.redirectUrl;
      } else {
        toast.error(data.message || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="guardian-payment">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p>Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="guardian-payment">
        <div className="error-container">
          <h2>Payment Information Not Available</h2>
          <p>The payment link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="guardian-payment">
      <section style={{ marginTop: '20px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard">
            <div className="panel-body text-center">
              <div className="container mx-auto py-8">
                <h4 style={{ color: '#12274e' }}>Pay for your property</h4>
                <h5 className="col-dark" style={{ marginBottom: '40px' }}>
                  Guardian Name: {paymentData.guardian.full_name || `${paymentData.guardian.user?.first_name} ${paymentData.guardian.user?.last_name}`}
                </h5>

                <img
                  src={paymentData.property.main_photo || '/images/default-property.jpg'}
                  alt="Property Image"
                  className="w-1/2 mx-auto img-responsive"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/default-property.jpg';
                  }}
                  style={{ borderRadius: '10px', maxWidth: '50%', marginBottom: '20px' }}
                />

                <h4 className="col-dark" style={{ marginTop: '20px' }}>
                  Property: {paymentData.property.name}
                </h4>

                <div className="row">
                  <div className="col-xs-6 text-left">
                    <h5 className="col-dark">Security Deposit: </h5>
                  </div>
                  <div className="col-xs-6 text-right">
                    <h5 className="col-dark">&pound;{paymentData.amounts.deposit.toFixed(2)}</h5>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-6 text-left">
                    <h5 className="col-dark">First Month Rent: </h5>
                  </div>
                  <div className="col-xs-6 text-right">
                    <h5 className="col-dark">&pound;{paymentData.amounts.firstMonthRent.toFixed(2)}</h5>
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-xs-6 text-left">
                    <h5 className="col-dark">Total due today: </h5>
                  </div>
                  <div className="col-xs-6 text-right">
                    <h5 className="col-dark">&pound;{paymentData.amounts.total.toFixed(2)}</h5>
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={processing}
                  className="btn btn-lg btn-success"
                  style={{ marginTop: '20px' }}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : (
                    'Pay by OpenBanking'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuardianPayment;